/**
 * Memory export: the text a user copies into another AI assistant, and the .md
 * file they can keep. The mirror of MD import — a downloaded file imports back
 * cleanly here, and pastes cleanly into Claude, ChatGPT or anything else.
 */

export type MemoryExportFormat = {
  /** The prompt pasted into another assistant. */
  prompt: (bodies: string[]) => string;
  /** The .md file's contents. */
  markdown: (bodies: string[]) => string;
  fileName: string;
};

const bullets = (bodies: string[]) => bodies.map((b) => `- ${b.trim()}`).join("\n");

const today = () => new Date().toISOString().slice(0, 10);

function slug(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "brand";
}

export function brandMemoryExport(brandName: string): MemoryExportFormat {
  return {
    prompt: (bodies) =>
      [
        `Below are the standing preferences for the brand "${brandName}": how its client and team want their content made, kept by our AI on Creative OS. Please remember them and apply them to any content work we do for ${brandName} — ideas, captions, scripts, briefs. If you can save memories, save each one. They are internal: never quote them in anything the client will read. If anything here conflicts with what I tell you later, what I tell you later wins.`,
        "",
        `${brandName} preferences:`,
        bullets(bodies),
      ].join("\n"),
    markdown: (bodies) =>
      [
        "---",
        `name: ${slug(brandName)}-brand-memory`,
        `description: Standing content preferences for ${brandName} — exported from Creative OS.`,
        `exported: ${today()}`,
        "---",
        "",
        `# ${brandName} — brand memory`,
        "",
        bullets(bodies),
        "",
      ].join("\n"),
    fileName: `${slug(brandName)}-brand-memory.md`,
  };
}

/** Saves text as a file through a throwaway link; no server round trip. */
export function downloadTextFile(fileName: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: "text/markdown;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
